import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { auditLogsAPI } from '../../services/api';
import { format } from 'date-fns';
import { formatLabel } from '../../utils/formatLabel';
import { ExportMenu } from '../SuperAdmin/ListTools';
import { fetchAllPages, formatExportDate } from '../../utils/exportData';
import {
  HiOutlineSearch,
  HiOutlineRefresh,
  HiOutlineShieldCheck,
  HiOutlineXCircle,
  HiOutlineUsers,
  HiOutlineOfficeBuilding,
  HiOutlineClipboardList,
  HiOutlineX,
} from 'react-icons/hi';

// Badge colours per audit action
const ACTION_STYLES = {
  create: 'bg-green-100 text-green-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
  soft_delete: 'bg-orange-100 text-orange-700',
  restore: 'bg-emerald-100 text-emerald-700',
  login: 'bg-indigo-100 text-indigo-700',
  logout: 'bg-gray-100 text-gray-600',
  export: 'bg-purple-100 text-purple-700',
  import: 'bg-purple-100 text-purple-700',
  view: 'bg-gray-100 text-gray-600',
  share: 'bg-teal-100 text-teal-700',
  download: 'bg-teal-100 text-teal-700',
  permission_change: 'bg-rose-100 text-rose-700',
  settings_change: 'bg-amber-100 text-amber-700',
};

const ACTION_LABELS = {
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
  soft_delete: 'Soft Delete',
  restore: 'Restore',
  login: 'Login',
  logout: 'Logout',
  export: 'Export',
  import: 'Import',
  view: 'View',
  share: 'Share',
  download: 'Download',
  permission_change: 'Permission Change',
  settings_change: 'Settings Change',
};

// Default entity types offered in the filter — merged with the types actually
// present in the data (stats.entityTypes) so new entity kinds appear too.
const BASE_ENTITY_TYPES = [
  'lead', 'user', 'call', 'workflow', 'campaign', 'communication', 'settings',
  'ticket', 'follow_up', 'task', 'meeting', 'visit', 'organization', 'plan', 'admin',
  'recording', 'report', 'target', 'email', 'notice', 'product', 'superadmin',
];

const StatCard = ({ icon: Icon, label, value, tone }) => {
  const tones = {
    indigo: 'bg-indigo-50 text-indigo-600',
    red: 'bg-red-50 text-red-600',
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
      <div className={`p-2.5 rounded-lg ${tones[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-lg font-semibold text-gray-900">{value ?? '—'}</p>
      </div>
    </div>
  );
};

const actionBadge = (action) => {
  const style = ACTION_STYLES[action] || 'bg-gray-100 text-gray-600';
  const label = ACTION_LABELS[action] || action;
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${style}`}>
      {label}
    </span>
  );
};

/**
 * Shared audit log viewer.
 *  mode="admin"      → organization-scoped logs (normal admin)
 *  mode="superadmin" → logs across all organizations, with org filter & column
 */
const AuditLogsView = ({ mode, orgOptions = [] }) => {
  const isSuperAdmin = mode === 'superadmin';

  const [filters, setFilters] = useState({
    search: '', action: '', entityType: '', status: '',
    startDate: '', endDate: '', organizationId: '', page: 1,
  });
  const [searchInput, setSearchInput] = useState('');

  // Debounce the free-text search so we don't hit the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.search === searchInput ? f : { ...f, search: searchInput, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const queryParams = { ...filters };
  delete queryParams.search;
  if (filters.search) queryParams.search = filters.search;
  if (!isSuperAdmin) delete queryParams.organizationId;

  const listQuery = useQuery({
    queryKey: ['audit-logs', mode, queryParams],
    queryFn: () => (isSuperAdmin
      ? auditLogsAPI.superAdminList(queryParams)
      : auditLogsAPI.list(queryParams)),
    refetchOnWindowFocus: false,
  });

  const statsQuery = useQuery({
    queryKey: ['audit-logs-stats', mode, queryParams],
    queryFn: () => (isSuperAdmin
      ? auditLogsAPI.superAdminStats(queryParams)
      : auditLogsAPI.stats(queryParams)),
    refetchOnWindowFocus: false,
  });

  const logs = listQuery.data?.data?.data || [];
  const pagination = listQuery.data?.data?.pagination || { page: 1, pages: 1, total: 0 };
  // axios body is { success, data: {...stats} }
  const stats = statsQuery.data?.data?.data || {};

  // Entity filter options: every type present in the data, plus the defaults
  const entityTypeOptions = Array.from(
    new Set([...(stats.entityTypes || []), ...BASE_ENTITY_TYPES]),
  ).sort();

  const hasFilters = filters.search || filters.action || filters.entityType ||
    filters.status || filters.startDate || filters.endDate ||
    (isSuperAdmin && filters.organizationId);

  const resetFilters = () => {
    setSearchInput('');
    setFilters({
      search: '', action: '', entityType: '', status: '',
      startDate: '', endDate: '', organizationId: '', page: 1,
    });
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return format(new Date(date), 'dd MMM yyyy, hh:mm a');
  };

  const userName = (log) => {
    const name = [log.user_id?.firstName, log.user_id?.lastName].filter(Boolean).join(' ');
    return name || log.user_email || 'Unknown';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Audit Logs</h3>
          <p className="text-sm text-gray-500 mt-1">
            {isSuperAdmin
              ? 'Security and activity trail across all organizations (read-only, immutable)'
              : 'Activity trail for your organization (read-only, immutable)'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportMenu
            fileName={isSuperAdmin ? 'audit-logs-all-organizations' : 'audit-logs'}
            getRows={() => fetchAllPages(
              isSuperAdmin ? auditLogsAPI.superAdminList : auditLogsAPI.list,
              queryParams,
              { pick: (body) => ({ rows: body.data || [], totalPages: body.pagination?.pages }) },
            )}
            columns={[
              { header: 'Time', value: (l) => formatExportDate(l.createdAt) },
              ...(isSuperAdmin ? [{ header: 'Organization', value: (l) => l.organizationId?.name }] : []),
              { header: 'User', value: userName },
              { header: 'User Email', value: (l) => l.user_email },
              { header: 'User Role', value: (l) => (l.user_role ? formatLabel(l.user_role) : '') },
              { header: 'Action', value: (l) => ACTION_LABELS[l.action] || l.action },
              { header: 'Entity', value: (l) => l.entity_type },
              { header: 'Description', value: (l) => l.description },
              { header: 'IP Address', value: (l) => l.ip_address },
              { header: 'Device', value: (l) => l.device },
              { header: 'Status', value: (l) => (l.status === 'failure' ? 'Failure' : 'Success') },
              { header: 'Error', value: (l) => l.error_message },
            ]}
          />
          <button
            onClick={() => listQuery.refetch()}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            <HiOutlineRefresh className={`w-4 h-4 ${listQuery.isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={HiOutlineClipboardList} label="Total Events" value={stats.total} tone="blue" />
        <StatCard icon={HiOutlineXCircle} label="Failures" value={stats.failures} tone="red" />
        <StatCard icon={HiOutlineUsers} label="Active Users" value={stats.activeUsers} tone="green" />
        {isSuperAdmin ? (
          <StatCard icon={HiOutlineOfficeBuilding} label="Organizations" value={stats.organizations} tone="indigo" />
        ) : (
          <StatCard icon={HiOutlineShieldCheck} label="Logins" value={stats.byAction?.login || 0} tone="indigo" />
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {isSuperAdmin && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Organization</label>
              <select
                value={filters.organizationId}
                onChange={(e) => setFilters({ ...filters, organizationId: e.target.value, page: 1 })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Organizations</option>
                {(orgOptions || []).map((org) => (
                  <option key={org.value} value={org.value}>{org.label}</option>
                ))}
              </select>
            </div>
          )}
          <div className={isSuperAdmin ? '' : 'sm:col-span-2 lg:col-span-2'}>
            <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
            <div className="relative">
              <HiOutlineSearch className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="User email or description..."
                className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Action</label>
            <select
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value, page: 1 })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Actions</option>
              {Object.entries(ACTION_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Entity</label>
            <select
              value={filters.entityType}
              onChange={(e) => setFilters({ ...filters, entityType: e.target.value, page: 1 })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Entities</option>
              {entityTypeOptions.map((t) => (
                <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Status</option>
              <option value="success">Success</option>
              <option value="failure">Failure</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">From Date</label>
            <input
              type="date"
              value={filters.startDate}
              max={filters.endDate || undefined}
              onChange={(e) => {
                const startDate = e.target.value;
                // Keep the range valid: From can never be after To
                const endDate = filters.endDate && startDate && startDate > filters.endDate ? startDate : filters.endDate;
                setFilters({ ...filters, startDate, endDate, page: 1 });
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">To Date</label>
            <input
              type="date"
              value={filters.endDate}
              min={filters.startDate || undefined}
              onChange={(e) => {
                const endDate = e.target.value;
                // Keep the range valid: To can never be before From
                const startDate = filters.startDate && endDate && endDate < filters.startDate ? endDate : filters.startDate;
                setFilters({ ...filters, startDate, endDate, page: 1 });
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex items-end">
            {hasFilters ? (
              <button
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 w-full justify-center px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <HiOutlineX className="w-4 h-4" />
                Clear Filters
              </button>
            ) : (
              <div className="w-full text-right pb-2 text-sm text-gray-500">
                {pagination.total} event{pagination.total !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
        {hasFilters && (
          <div className="flex items-center justify-end mt-3 pt-3 border-t border-gray-100">
            <span className="text-sm text-gray-500">
              {pagination.total} event{pagination.total !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* Table */}
      {listQuery.isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : logs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <HiOutlineShieldCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h4 className="text-gray-700 font-medium mb-1">No audit events found</h4>
          <p className="text-sm text-gray-500">
            {hasFilters
              ? 'Try adjusting or clearing the filters above.'
              : 'Audit events (logins, lead changes, settings updates...) will appear here as they happen.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-auto max-h-[calc(100vh-220px)]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-200 bg-gray-50 text-left">
                <th className="px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Time</th>
                {isSuperAdmin && (
                  <th className="px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Organization</th>
                )}
                <th className="px-4 py-3 font-medium text-gray-500 whitespace-nowrap">User</th>
                <th className="px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Action</th>
                <th className="px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Entity</th>
                <th className="px-4 py-3 font-medium text-gray-500">Description</th>
                <th className="px-4 py-3 font-medium text-gray-500 whitespace-nowrap">IP / Device</th>
                <th className="px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Status</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log._id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(log.createdAt)}</td>
                  {isSuperAdmin && (
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {log.organizationId?.name || '—'}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 whitespace-nowrap flex items-center gap-1.5">
                      {userName(log)}
                      {log.user_role === 'super_admin' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-rose-100 text-rose-700 whitespace-nowrap">
                          Super Admin
                        </span>
                      )}
                    </div>
                    {(log.user_email || log.user_role) && (
                      <div className="text-xs text-gray-400 truncate max-w-[180px]">
                        {[log.user_email, log.user_role && formatLabel(log.user_role)].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">{actionBadge(log.action)}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                    {log.entity_type
                      ? log.entity_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-700 max-w-[320px]">
                    <span className="line-clamp-2">{log.description || '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {log.ip_address || '—'}
                    {log.device && <span className="text-xs text-gray-400 ml-1 capitalize">({log.device})</span>}
                  </td>
                  <td className="px-4 py-3">
                    {log.status === 'failure' ? (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700 whitespace-nowrap"
                        title={log.error_message || undefined}>
                        Failure
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700 whitespace-nowrap">
                        Success
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setFilters((f) => ({ ...f, page: Math.max(1, f.page - 1) }))}
            disabled={pagination.page <= 1}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            onClick={() => setFilters((f) => ({ ...f, page: Math.min(pagination.pages, f.page + 1) }))}
            disabled={pagination.page >= pagination.pages}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditLogsView;